/**
 * MAZE PATROL — fifth cartridge, maze-chase platformer (MVP).
 *
 * Heritage: Pac-Man (1980) — corridor maze, dot collection, ghost-style
 * 4-direction AI with power-pellet reversal — plus Lady Bug (1981) for
 * the rotating-gate flavor. Original mechanic: **the whole 9×9 maze
 * rotates 90° every 60 seconds.** Players have to navigate by the
 * current layout, not muscle memory. Spawn positions are 4-way
 * symmetric so rotation never strands an entity in a wall.
 *
 * Layout: 480×270 canvas. The maze is a centered 270×270 square (9×9
 * cells at 30 px each); the side gutters (~105 px each) hold the HUD
 * + minimap + rotation countdown chrome. Per the cabinet's
 * landscape-only rule, NOT black bars.
 *
 * Determinism: all randomness routes through ctx.random (Wanderer AI
 * turn choices, BFS tie-breaks for Pursuer/Ambusher, thief jailbreak
 * direction).
 */

import type {
  GameContext,
  GameInstance,
  InputState,
} from '@arcraz/arcadia-types';
import {
  ALARM_PANEL,
  ALARM_PANEL_DIM,
  ALARM_WASH,
  BADGE,
  BADGE_DARK,
  FIELD_BG,
  FLASH_HIT,
  GUTTER_BG,
  HUD_ALARM_BAR,
  HUD_ALERT,
  HUD_LABEL,
  HUD_LEVEL,
  HUD_LIVES,
  HUD_SCORE,
  JAIL_BAR,
  JAIL_BG,
  PLAYER_BODY,
  PLAYER_LIGHT,
  PLAYER_OUTLINE,
  ROTATION_WASH,
  THIEF_AMBUSHER,
  THIEF_EYES,
  THIEF_PATROLLER,
  THIEF_PUPIL,
  THIEF_PURSUER,
  THIEF_SCARED,
  THIEF_SCARED_FLASH,
  THIEF_WANDERER,
  WALL,
  WALL_EDGE,
} from './palette.js';
import {
  CELL_PX,
  CellKind,
  GRID_H,
  GRID_W,
  PIVOT_CELL,
  THIEF_SPAWN_CELLS,
  cellAt,
  countBadges,
  freshLayout,
  isWalkable,
  rotateCw90,
  rotateCoordCw90,
  rotateDirCw90,
  setCell,
  type CellKindValue,
  type Layout,
} from './maze.js';

// --- Canvas + playfield -----------------------------------------------
const WIDTH = 480;
const HEIGHT = 270;
const MAZE_PX = GRID_W * CELL_PX; // 270
const MAZE_X0 = (WIDTH - MAZE_PX) / 2; // 105
const MAZE_Y0 = (HEIGHT - MAZE_PX) / 2; // 0

// --- Player -----------------------------------------------------------
const PLAYER_SPEED = 2; // px/tick → 30/2 = 15 ticks per cell (~0.25s/cell)
const PLAYER_RADIUS = 9; // visual + collision radius

// --- Thieves ----------------------------------------------------------
const THIEF_SPEED = 1.6; // 80% of player → catchable but tense
const THIEF_SCARED_SPEED = 1.1; // slower when alarm is active
const THIEF_RADIUS = 9;
type ThiefKind = 'pursuer' | 'ambusher' | 'patroller' | 'wanderer';
const THIEF_KINDS: ReadonlyArray<ThiefKind> = [
  'pursuer',
  'ambusher',
  'patroller',
  'wanderer',
];

// --- Patroller waypoints ----------------------------------------------
// Patroller cycles a fixed 4-cell loop. Picked so the loop is 4-way
// symmetric, which means rotation maps the loop onto itself (a
// different rotation of the same 4 cells), so the patroller never
// finds itself off-route after a rotation event.
const PATROL_WAYPOINTS: ReadonlyArray<{ r: number; c: number }> = [
  { r: 2, c: 2 },
  { r: 2, c: 6 },
  { r: 6, c: 6 },
  { r: 6, c: 2 },
];

// --- Alarm ------------------------------------------------------------
const ALARM_DURATION = 480; // 8 seconds @ 60Hz
const ALARM_WARN_TICKS = 90; // last 1.5s flash white-blue alternation
const ALARM_TAG_SCORE = 200;

// --- Rotation ---------------------------------------------------------
const ROTATION_INTERVAL = 3600; // 60s @ 60Hz
const ROTATION_WARN_TICKS = 90; // 1.5s warning before rotation fires
const ROTATION_FLASH_TICKS = 18; // brief screen flash on rotation

// --- Lives / scoring --------------------------------------------------
const LIVES_START = 3;
const RESPAWN_INVULN = 75; // 1.25s
const DEATH_FLASH_TICKS = 22;
const BADGE_SCORE = 10;
const LEVEL_CLEAR_BONUS_BASE = 1000;

// --- Direction --------------------------------------------------------
type Dir = 'up' | 'down' | 'left' | 'right' | 'none';
const DIR_VEC: Record<Dir, { dr: number; dc: number }> = {
  up: { dr: -1, dc: 0 },
  down: { dr: 1, dc: 0 },
  left: { dr: 0, dc: -1 },
  right: { dr: 0, dc: 1 },
  none: { dr: 0, dc: 0 },
};

type Phase = 'playing' | 'crashed' | 'level-clear' | 'rotating' | 'game-over';

interface Entity {
  /** Cell-space center (continuous): r,c grow as the entity walks. */
  r: number;
  c: number;
  /** Current locked-in direction. Cleared to 'none' when the entity
   *  reaches an intersection and needs to repick. */
  dir: Dir;
  /** Pending direction queued by the player — applied at the next cell
   *  center if the corridor allows it. Unused for thieves. */
  queuedDir: Dir;
}

interface Thief extends Entity {
  kind: ThiefKind;
  spawn: { r: number; c: number };
  /** True while a tagged thief is in the jail "respawn" timer. */
  jailed: boolean;
  jailedTicks: number;
  /** Patroller-only: index into PATROL_WAYPOINTS the thief is heading toward. */
  waypointIdx: number;
}

const JAIL_RELEASE_TICKS = 60; // 1s — short enough that alarm has stakes

export class MazePatrolInstance implements GameInstance {
  private readonly ctx: GameContext;

  private layout: Layout = freshLayout();
  private tickCount = 0;
  private phase: Phase = 'playing';
  private deathFlash = 0;
  private levelClearTicks = 0;
  private rotatingFlash = 0;

  private score = 0;
  private lives = LIVES_START;
  private level = 1;
  private finalScore = 0;
  private finalLevel = 1;
  private badgesRemaining: number;

  private player: Entity;
  private invuln = RESPAWN_INVULN;
  private thieves: Thief[] = [];

  private alarmTicksRemaining = 0;
  private rotationCountdown = ROTATION_INTERVAL;

  private inputDir: Dir = 'none';
  private restartArmed = false;

  constructor(ctx: GameContext) {
    this.ctx = ctx;
    this.player = makeEntity(PIVOT_CELL.r, PIVOT_CELL.c);
    this.thieves = makeThieves();
    this.badgesRemaining = countBadges(this.layout);
  }

  // -- GameInstance contract --------------------------------------------

  input(state: InputState): void {
    if (this.phase === 'game-over') {
      if (state.justPressed.has('fireA') || state.justPressed.has('start')) {
        this.restartArmed = true;
      }
      return;
    }
    // Player input queues a direction; it's applied at the next cell
    // center if the corridor allows. This makes the controls feel
    // responsive without letting the player drift through walls.
    if (state.up) this.inputDir = 'up';
    else if (state.down) this.inputDir = 'down';
    else if (state.left) this.inputDir = 'left';
    else if (state.right) this.inputDir = 'right';
  }

  update(_dt: number): void {
    this.tickCount += 1;

    if (this.phase === 'game-over') return;

    if (this.phase === 'crashed') {
      this.deathFlash -= 1;
      if (this.deathFlash <= 0) {
        if (this.lives > 0) {
          this.respawn();
        } else {
          this.finalScore = this.score;
          this.finalLevel = this.level;
          this.phase = 'game-over';
        }
      }
      return;
    }

    if (this.phase === 'level-clear') {
      this.levelClearTicks -= 1;
      if (this.levelClearTicks <= 0) {
        this.startLevel(this.level + 1);
      }
      return;
    }

    if (this.phase === 'rotating') {
      this.rotatingFlash -= 1;
      if (this.rotatingFlash <= 0) {
        this.phase = 'playing';
      }
      return;
    }

    if (this.invuln > 0) this.invuln -= 1;

    // Rotation countdown.
    this.rotationCountdown -= 1;
    // Edge-trigger the warn warble at the moment we cross INTO the
    // warn window. Firing it once (not every tick) keeps the audio bus
    // from lawnmowering during the 1.5s telegraph.
    if (this.rotationCountdown === ROTATION_WARN_TICKS) {
      this.ctx.audio.sfx('rotate-warn');
    }
    if (this.rotationCountdown <= 0) {
      this.performRotation();
      return; // skip the rest of the tick — entities just teleported
    }

    // Alarm countdown.
    if (this.alarmTicksRemaining > 0) this.alarmTicksRemaining -= 1;

    this.updatePlayer();
    this.updateThieves();
    this.checkCollisions();

    if (this.badgesRemaining === 0) {
      this.score += LEVEL_CLEAR_BONUS_BASE * this.level;
      this.phase = 'level-clear';
      this.levelClearTicks = 90;
      this.ctx.audio.sfx('level-up');
    }
  }

  render(g: CanvasRenderingContext2D): void {
    // Full canvas background — slightly different tone in the gutters
    // vs the maze so the column reads as the active surface.
    g.fillStyle = GUTTER_BG;
    g.fillRect(0, 0, WIDTH, HEIGHT);
    g.fillStyle = FIELD_BG;
    g.fillRect(MAZE_X0, MAZE_Y0, MAZE_PX, MAZE_PX);

    this.renderGutterChrome(g);
    this.renderMaze(g);
    this.renderThieves(g);
    this.renderPlayer(g);

    // Alarm wash overlay.
    if (this.alarmTicksRemaining > 0) {
      g.fillStyle = ALARM_WASH;
      g.fillRect(MAZE_X0, MAZE_Y0, MAZE_PX, MAZE_PX);
    }
    // Rotation flash.
    if (this.rotatingFlash > 0) {
      g.fillStyle = ROTATION_WASH;
      g.fillRect(MAZE_X0, MAZE_Y0, MAZE_PX, MAZE_PX);
    }
    // Death flash.
    if (this.phase === 'crashed' && this.deathFlash > DEATH_FLASH_TICKS - 6) {
      g.fillStyle = FLASH_HIT;
      g.fillRect(MAZE_X0, MAZE_Y0, MAZE_PX, MAZE_PX);
    }

    this.renderHUD(g);

    if (this.phase === 'level-clear') this.renderLevelBanner(g);
    if (this.phase === 'game-over') this.renderGameOver(g);
  }

  resize(): void {
    // Fixed canvas.
  }
  pause(): void {
    // No sustained voices in this cartridge yet.
  }
  resume(): void {}
  teardown(): void {
    this.thieves = [];
  }

  getScore(): number {
    return Math.floor(
      this.phase === 'game-over' ? this.finalScore : this.score
    );
  }
  getFinalScore(): number {
    return Math.floor(
      this.phase === 'game-over' ? this.finalScore : this.score
    );
  }
  isGameOver(): boolean {
    return this.phase === 'game-over';
  }
  shouldRestart(): boolean {
    return this.phase === 'game-over' && this.restartArmed;
  }

  // -- State transitions -----------------------------------------------

  private startLevel(level: number): void {
    this.level = level;
    this.layout = freshLayout();
    this.badgesRemaining = countBadges(this.layout);
    this.alarmTicksRemaining = 0;
    this.rotationCountdown = ROTATION_INTERVAL;
    this.player = makeEntity(PIVOT_CELL.r, PIVOT_CELL.c);
    this.thieves = makeThieves();
    this.invuln = RESPAWN_INVULN;
    this.inputDir = 'none';
    this.phase = 'playing';
  }

  private respawn(): void {
    this.player = makeEntity(PIVOT_CELL.r, PIVOT_CELL.c);
    this.invuln = RESPAWN_INVULN;
    this.alarmTicksRemaining = 0;
    this.inputDir = 'none';
    for (const t of this.thieves) {
      t.r = t.spawn.r;
      t.c = t.spawn.c;
      t.dir = 'none';
      t.jailed = false;
      t.jailedTicks = 0;
    }
    this.phase = 'playing';
  }

  private performRotation(): void {
    rotateCw90(this.layout);
    // Rotate entity positions in lockstep with the layout.
    const pNew = rotateCoordCw90(this.player.r, this.player.c);
    this.player.r = pNew.r;
    this.player.c = pNew.c;
    const pdir = DIR_VEC[this.player.dir];
    const pdNew = rotateDirCw90(pdir.dr, pdir.dc);
    this.player.dir = vecToDir(pdNew.dr, pdNew.dc);
    this.player.queuedDir = this.player.dir;
    for (const t of this.thieves) {
      const tn = rotateCoordCw90(t.r, t.c);
      t.r = tn.r;
      t.c = tn.c;
      const td = DIR_VEC[t.dir];
      const tdn = rotateDirCw90(td.dr, td.dc);
      t.dir = vecToDir(tdn.dr, tdn.dc);
      const sn = rotateCoordCw90(t.spawn.r, t.spawn.c);
      t.spawn = sn;
    }
    this.rotationCountdown = ROTATION_INTERVAL;
    this.phase = 'rotating';
    this.rotatingFlash = ROTATION_FLASH_TICKS;
    this.ctx.audio.sfx('rotate');
  }

  // -- Player movement -------------------------------------------------

  private updatePlayer(): void {
    const p = this.player;
    const atCenter = isAtCellCenter(p);

    if (atCenter) {
      const cr = Math.round(p.r);
      const cc = Math.round(p.c);
      p.r = cr;
      p.c = cc;

      // Try to apply input direction if the path is open.
      const wanted = this.inputDir;
      if (wanted !== 'none') {
        const v = DIR_VEC[wanted];
        if (isWalkable(cellAt(this.layout, cr + v.dr, cc + v.dc))) {
          p.dir = wanted;
        }
      }
      // Stop if current direction is now blocked by a wall.
      if (p.dir !== 'none') {
        const v = DIR_VEC[p.dir];
        if (!isWalkable(cellAt(this.layout, cr + v.dr, cc + v.dc))) {
          p.dir = 'none';
        }
      }

      // Collect badge / alarm on the cell we're at.
      const cell = cellAt(this.layout, cr, cc);
      if (cell === CellKind.BADGE) {
        setCell(this.layout, cr, cc, CellKind.EMPTY);
        this.badgesRemaining -= 1;
        this.score += BADGE_SCORE;
        this.ctx.audio.sfx('badge');
      } else if (cell === CellKind.ALARM) {
        setCell(this.layout, cr, cc, CellKind.EMPTY);
        this.alarmTicksRemaining = ALARM_DURATION;
        this.ctx.audio.sfx('alarm-on');
      }
    }

    if (p.dir !== 'none') {
      const v = DIR_VEC[p.dir];
      const step = PLAYER_SPEED / CELL_PX;
      p.r += v.dr * step;
      p.c += v.dc * step;
    }
  }

  // -- Thief AI --------------------------------------------------------

  private updateThieves(): void {
    for (const t of this.thieves) {
      if (t.jailed) {
        t.jailedTicks -= 1;
        if (t.jailedTicks <= 0) {
          t.jailed = false;
          // Release at jail cell and pick a random valid direction.
          t.r = PIVOT_CELL.r;
          t.c = PIVOT_CELL.c;
          t.dir = this.pickRandomValidDir(t.r, t.c, 'none');
        }
        continue;
      }
      const atCenter = isAtCellCenter(t);
      if (atCenter) {
        t.r = Math.round(t.r);
        t.c = Math.round(t.c);
        t.dir = this.pickThiefDir(t);
      }
      if (t.dir !== 'none') {
        const v = DIR_VEC[t.dir];
        const speed = this.alarmTicksRemaining > 0 ? THIEF_SCARED_SPEED : THIEF_SPEED;
        const step = speed / CELL_PX;
        t.r += v.dr * step;
        t.c += v.dc * step;
      }
    }
  }

  /** Per-AI direction picker. Always called at a cell center. Falls
   *  back to a random valid direction if the AI's intent is blocked. */
  private pickThiefDir(t: Thief): Dir {
    const cr = Math.round(t.r);
    const cc = Math.round(t.c);

    // While scared, all thieves wander randomly — alarm makes them
    // unpredictable to dodge, not strategic.
    if (this.alarmTicksRemaining > 0) {
      return this.pickRandomValidDir(cr, cc, t.dir);
    }

    if (t.kind === 'pursuer') {
      return this.pickBfsDir(cr, cc, t.dir, {
        r: Math.round(this.player.r),
        c: Math.round(this.player.c),
      });
    }
    if (t.kind === 'ambusher') {
      const pdir = DIR_VEC[this.player.dir];
      const target = {
        r: Math.round(this.player.r) + pdir.dr * 4,
        c: Math.round(this.player.c) + pdir.dc * 4,
      };
      return this.pickBfsDir(cr, cc, t.dir, target);
    }
    if (t.kind === 'patroller') {
      const wp = PATROL_WAYPOINTS[t.waypointIdx];
      if (!wp) return 'none';
      if (cr === wp.r && cc === wp.c) {
        t.waypointIdx = (t.waypointIdx + 1) % PATROL_WAYPOINTS.length;
      }
      const wp2 = PATROL_WAYPOINTS[t.waypointIdx] ?? wp;
      return this.pickBfsDir(cr, cc, t.dir, wp2);
    }
    return this.pickRandomValidDir(cr, cc, t.dir);
  }

  /** Single-step BFS toward target — returns the direction whose
   *  next-cell starts the shortest path. Excludes the direct-reverse
   *  of `currentDir` so thieves don't oscillate (Pac-Man precedent).
   *  Falls back to a random valid direction if no path exists. */
  private pickBfsDir(
    fromR: number,
    fromC: number,
    currentDir: Dir,
    target: { r: number; c: number }
  ): Dir {
    const reverse = oppositeDir(currentDir);
    // Clamp target to bounds — ambusher's 4-cells-ahead can leave the grid.
    const tr = Math.max(0, Math.min(GRID_H - 1, target.r));
    const tc = Math.max(0, Math.min(GRID_W - 1, target.c));

    const visited = new Uint8Array(GRID_H * GRID_W);
    const fromIdx = (r: number, c: number) => r * GRID_W + c;

    interface Q {
      r: number;
      c: number;
      first: Dir;
    }
    const queue: Q[] = [];

    // Seed the queue with each available first-step direction.
    const firstDirs: Dir[] = ['up', 'down', 'left', 'right'];
    for (const d of firstDirs) {
      if (d === reverse) continue;
      const v = DIR_VEC[d];
      const nr = fromR + v.dr;
      const nc = fromC + v.dc;
      if (!isWalkable(cellAt(this.layout, nr, nc))) continue;
      visited[fromIdx(nr, nc)] = 1;
      queue.push({ r: nr, c: nc, first: d });
    }

    // BFS for the target.
    let head = 0;
    while (head < queue.length) {
      const item = queue[head++];
      if (!item) break;
      if (item.r === tr && item.c === tc) return item.first;
      const dirs: Dir[] = ['up', 'down', 'left', 'right'];
      for (const d of dirs) {
        const v = DIR_VEC[d];
        const nr = item.r + v.dr;
        const nc = item.c + v.dc;
        if (nr < 0 || nr >= GRID_H || nc < 0 || nc >= GRID_W) continue;
        if (visited[fromIdx(nr, nc)]) continue;
        if (!isWalkable(cellAt(this.layout, nr, nc))) continue;
        visited[fromIdx(nr, nc)] = 1;
        queue.push({ r: nr, c: nc, first: item.first });
      }
    }
    return this.pickRandomValidDir(fromR, fromC, currentDir);
  }

  private pickRandomValidDir(fromR: number, fromC: number, currentDir: Dir): Dir {
    const reverse = oppositeDir(currentDir);
    const opts: Dir[] = [];
    for (const d of ['up', 'down', 'left', 'right'] as const) {
      if (d === reverse) continue;
      const v = DIR_VEC[d];
      if (isWalkable(cellAt(this.layout, fromR + v.dr, fromC + v.dc))) opts.push(d);
    }
    if (opts.length === 0) {
      // Dead end — allow reverse.
      if (reverse !== 'none') return reverse;
      return 'none';
    }
    const idx = Math.floor(this.ctx.random.next() * opts.length);
    return opts[idx] ?? 'none';
  }

  // -- Collisions ------------------------------------------------------

  private checkCollisions(): void {
    if (this.invuln > 0) return;
    const px = MAZE_X0 + this.player.c * CELL_PX + CELL_PX / 2;
    const py = MAZE_Y0 + this.player.r * CELL_PX + CELL_PX / 2;
    for (const t of this.thieves) {
      if (t.jailed) continue;
      const tx = MAZE_X0 + t.c * CELL_PX + CELL_PX / 2;
      const ty = MAZE_Y0 + t.r * CELL_PX + CELL_PX / 2;
      const dist2 =
        (tx - px) * (tx - px) + (ty - py) * (ty - py);
      const r = PLAYER_RADIUS + THIEF_RADIUS;
      if (dist2 > r * r) continue;

      if (this.alarmTicksRemaining > 0) {
        // Tag the thief — back to jail.
        t.jailed = true;
        t.jailedTicks = JAIL_RELEASE_TICKS;
        t.r = PIVOT_CELL.r;
        t.c = PIVOT_CELL.c;
        this.score += ALARM_TAG_SCORE;
        this.ctx.audio.sfx('thief-tag');
      } else {
        // Death.
        this.lives -= 1;
        this.phase = 'crashed';
        this.deathFlash = DEATH_FLASH_TICKS;
        this.ctx.audio.sfx('death');
        return;
      }
    }
  }

  // -- Rendering -------------------------------------------------------

  private renderMaze(g: CanvasRenderingContext2D): void {
    // Pulse the alarm panel during play — readable from across the
    // playfield. Two cosines so the animation has a slight asymmetry
    // and doesn't read as a metronome.
    const panelPulse = 0.6 + 0.4 * Math.sin(this.tickCount * 0.12);

    for (let r = 0; r < GRID_H; r++) {
      for (let c = 0; c < GRID_W; c++) {
        const kind = cellAt(this.layout, r, c);
        const x = MAZE_X0 + c * CELL_PX;
        const y = MAZE_Y0 + r * CELL_PX;
        if (kind === CellKind.WALL) {
          g.fillStyle = WALL;
          g.fillRect(x, y, CELL_PX, CELL_PX);
          g.fillStyle = WALL_EDGE;
          g.fillRect(x, y + CELL_PX - 3, CELL_PX, 3);
          g.fillRect(x + CELL_PX - 3, y, 3, CELL_PX);
        } else if (kind === CellKind.BADGE) {
          g.fillStyle = BADGE;
          g.fillRect(x + CELL_PX / 2 - 3, y + CELL_PX / 2 - 3, 6, 6);
          g.fillStyle = BADGE_DARK;
          g.fillRect(x + CELL_PX / 2 - 3, y + CELL_PX / 2 + 1, 6, 2);
        } else if (kind === CellKind.ALARM) {
          g.fillStyle = panelPulse > 0.6 ? ALARM_PANEL : ALARM_PANEL_DIM;
          const radius = 6 + 2 * panelPulse;
          g.beginPath();
          g.arc(x + CELL_PX / 2, y + CELL_PX / 2, radius, 0, Math.PI * 2);
          g.fill();
        }
      }
    }

    // Jail bars rendered over the pivot cell so the "front desk" is
    // visually distinct from a plain empty cell.
    const jailX = MAZE_X0 + PIVOT_CELL.c * CELL_PX;
    const jailY = MAZE_Y0 + PIVOT_CELL.r * CELL_PX;
    g.fillStyle = JAIL_BG;
    g.fillRect(jailX + 4, jailY + 4, CELL_PX - 8, CELL_PX - 8);
    g.fillStyle = JAIL_BAR;
    for (let i = 0; i < 3; i++) {
      g.fillRect(jailX + 8 + i * 7, jailY + 6, 2, CELL_PX - 12);
    }
  }

  private renderPlayer(g: CanvasRenderingContext2D): void {
    const p = this.player;
    if (this.invuln > 0 && this.invuln % 8 < 4) return; // blink during invuln
    const cx = MAZE_X0 + p.c * CELL_PX + CELL_PX / 2;
    const cy = MAZE_Y0 + p.r * CELL_PX + CELL_PX / 2;

    // Flashlight cone in the facing direction — soft yellow wash.
    const v = DIR_VEC[p.dir];
    if (p.dir !== 'none') {
      g.fillStyle = PLAYER_LIGHT;
      const angle = Math.atan2(v.dr, v.dc);
      g.save();
      g.translate(cx, cy);
      g.rotate(angle);
      g.beginPath();
      g.moveTo(0, 0);
      g.lineTo(20, -8);
      g.lineTo(20, 8);
      g.closePath();
      g.fill();
      g.restore();
    }

    // Body — circle.
    g.fillStyle = PLAYER_BODY;
    g.beginPath();
    g.arc(cx, cy, PLAYER_RADIUS, 0, Math.PI * 2);
    g.fill();
    g.strokeStyle = PLAYER_OUTLINE;
    g.lineWidth = 1;
    g.stroke();
  }

  private renderThieves(g: CanvasRenderingContext2D): void {
    const scaredFlash =
      this.alarmTicksRemaining > 0 &&
      this.alarmTicksRemaining < ALARM_WARN_TICKS &&
      Math.floor(this.tickCount / 6) % 2 === 0;
    for (const t of this.thieves) {
      const cx = MAZE_X0 + t.c * CELL_PX + CELL_PX / 2;
      const cy = MAZE_Y0 + t.r * CELL_PX + CELL_PX / 2;
      let color = thiefColor(t.kind);
      if (this.alarmTicksRemaining > 0) {
        color = scaredFlash ? THIEF_SCARED_FLASH : THIEF_SCARED;
      }
      if (t.jailed) {
        // Tagged thieves render dim inside the jail — half-alpha effect
        // achieved via a desaturated outline only.
        g.strokeStyle = color;
        g.lineWidth = 1;
        g.beginPath();
        g.arc(cx, cy, THIEF_RADIUS, 0, Math.PI * 2);
        g.stroke();
        continue;
      }
      g.fillStyle = color;
      g.beginPath();
      g.arc(cx, cy, THIEF_RADIUS, 0, Math.PI * 2);
      g.fill();
      // Eyes — direction-indicator dots.
      const v = DIR_VEC[t.dir];
      g.fillStyle = THIEF_EYES;
      g.fillRect(cx - 4 + v.dc * 2, cy - 2 + v.dr * 2, 3, 3);
      g.fillRect(cx + 1 + v.dc * 2, cy - 2 + v.dr * 2, 3, 3);
      g.fillStyle = THIEF_PUPIL;
      g.fillRect(cx - 3 + v.dc * 2, cy - 1 + v.dr * 2, 1, 1);
      g.fillRect(cx + 2 + v.dc * 2, cy - 1 + v.dr * 2, 1, 1);
    }
  }

  private renderGutterChrome(g: CanvasRenderingContext2D): void {
    // Subtle vertical separator beams on the maze edges.
    g.fillStyle = WALL_EDGE;
    g.fillRect(MAZE_X0 - 4, 0, 2, HEIGHT);
    g.fillRect(MAZE_X0 + MAZE_PX + 2, 0, 2, HEIGHT);
  }

  private renderHUD(g: CanvasRenderingContext2D): void {
    g.textAlign = 'left';
    g.textBaseline = 'top';

    // Left gutter — SCORE / BADGES / LEVEL / LIVES / ALARM bar.
    const lx = 6;
    let y = 8;
    g.fillStyle = HUD_LABEL;
    g.font = '8px ui-monospace, monospace';
    g.fillText('SCORE', lx, y);
    y += 10;
    g.fillStyle = HUD_SCORE;
    g.font = 'bold 12px ui-monospace, monospace';
    g.fillText(String(Math.floor(this.score)).padStart(6, '0'), lx, y);
    y += 18;

    g.font = '8px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    g.fillText('BADGES', lx, y);
    y += 10;
    g.fillStyle = HUD_SCORE;
    g.font = 'bold 12px ui-monospace, monospace';
    g.fillText(String(this.badgesRemaining).padStart(3, '0'), lx, y);
    y += 18;

    g.font = '8px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    g.fillText('LEVEL', lx, y);
    y += 10;
    g.fillStyle = HUD_LEVEL;
    g.font = 'bold 12px ui-monospace, monospace';
    g.fillText(String(this.level).padStart(2, '0'), lx, y);
    y += 18;

    g.font = '8px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    g.fillText('LIVES', lx, y);
    y += 10;
    g.fillStyle = HUD_LIVES;
    for (let i = 0; i < this.lives; i++) {
      g.beginPath();
      g.arc(lx + 4 + i * 10, y + 3, 3, 0, Math.PI * 2);
      g.fill();
    }
    y += 14;

    // Alarm bar (active state).
    if (this.alarmTicksRemaining > 0) {
      g.fillStyle = HUD_LABEL;
      g.fillText('ALARM', lx, y);
      y += 10;
      const frac = this.alarmTicksRemaining / ALARM_DURATION;
      g.fillStyle = HUD_ALARM_BAR;
      g.fillRect(lx, y, 80 * frac, 4);
      g.strokeStyle = HUD_LABEL;
      g.lineWidth = 1;
      g.strokeRect(lx + 0.5, y + 0.5, 79, 3);
    }

    // Right gutter — ROTATING IN countdown + minimap.
    const rx = MAZE_X0 + MAZE_PX + 8;
    let ry = 8;
    g.fillStyle = HUD_LABEL;
    g.font = '8px ui-monospace, monospace';
    g.fillText('ROTATE IN', rx, ry);
    ry += 10;
    const secs = Math.ceil(this.rotationCountdown / 60);
    const warn = this.rotationCountdown <= ROTATION_WARN_TICKS;
    g.fillStyle =
      warn && Math.floor(this.tickCount / 6) % 2 === 0 ? HUD_ALERT : HUD_SCORE;
    g.font = 'bold 14px ui-monospace, monospace';
    g.fillText(`${String(secs).padStart(2, '0')}s`, rx, ry);
    ry += 22;

    // Minimap.
    g.fillStyle = HUD_LABEL;
    g.font = '7px ui-monospace, monospace';
    g.fillText('MAP', rx, ry);
    ry += 10;
    this.renderMinimap(g, rx, ry);
  }

  private renderMinimap(g: CanvasRenderingContext2D, x: number, y: number): void {
    const cell = 8; // 9*8 = 72px square minimap
    for (let r = 0; r < GRID_H; r++) {
      for (let c = 0; c < GRID_W; c++) {
        const kind = cellAt(this.layout, r, c);
        const mx = x + c * cell;
        const my = y + r * cell;
        if (kind === CellKind.WALL) {
          g.fillStyle = WALL_EDGE;
          g.fillRect(mx, my, cell - 1, cell - 1);
        } else if (kind === CellKind.BADGE) {
          g.fillStyle = BADGE_DARK;
          g.fillRect(mx + cell / 2 - 1, my + cell / 2 - 1, 2, 2);
        } else if (kind === CellKind.ALARM) {
          g.fillStyle = ALARM_PANEL;
          g.fillRect(mx + cell / 2 - 1, my + cell / 2 - 1, 3, 3);
        }
      }
    }
    // Player on minimap.
    const pmx = x + Math.round(this.player.c * cell) + cell / 2 - 1;
    const pmy = y + Math.round(this.player.r * cell) + cell / 2 - 1;
    g.fillStyle = PLAYER_BODY;
    g.fillRect(pmx - 1, pmy - 1, 3, 3);
    // Thieves on minimap.
    for (const t of this.thieves) {
      if (t.jailed) continue;
      const tmx = x + Math.round(t.c * cell) + cell / 2 - 1;
      const tmy = y + Math.round(t.r * cell) + cell / 2 - 1;
      g.fillStyle = this.alarmTicksRemaining > 0 ? THIEF_SCARED : thiefColor(t.kind);
      g.fillRect(tmx, tmy, 2, 2);
    }
  }

  private renderLevelBanner(g: CanvasRenderingContext2D): void {
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = 'bold 16px ui-monospace, monospace';
    g.fillStyle = HUD_SCORE;
    g.fillText(
      `LEVEL ${this.level} SECURE`,
      MAZE_X0 + MAZE_PX / 2,
      MAZE_Y0 + MAZE_PX / 2 - 8
    );
    g.font = '10px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    g.fillText(
      `+${LEVEL_CLEAR_BONUS_BASE * this.level} CLEAR BONUS`,
      MAZE_X0 + MAZE_PX / 2,
      MAZE_Y0 + MAZE_PX / 2 + 10
    );
    g.textBaseline = 'top';
    g.textAlign = 'left';
  }

  private renderGameOver(g: CanvasRenderingContext2D): void {
    // Full-canvas scrim — dim the frozen playfield + HUD behind so the
    // banner reads cleanly. Same opacity Stardust uses for visual
    // consistency across cartridge end-screens.
    g.fillStyle = 'rgba(0, 0, 0, 0.72)';
    g.fillRect(0, 0, WIDTH, HEIGHT);

    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = 'bold 22px ui-monospace, monospace';
    g.fillStyle = HUD_ALERT;
    g.fillText('GAME OVER', WIDTH / 2, HEIGHT / 2 - 22);

    g.font = '11px ui-monospace, monospace';
    g.fillStyle = HUD_SCORE;
    g.fillText(
      `SCORE  ${String(Math.floor(this.finalScore)).padStart(6, '0')}`,
      WIDTH / 2,
      HEIGHT / 2 + 2
    );
    g.fillStyle = HUD_LEVEL;
    g.fillText(`LEVEL  ${this.finalLevel}`, WIDTH / 2, HEIGHT / 2 + 18);

    g.font = '10px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    if (Math.floor(this.tickCount / 30) % 2 === 0) {
      g.fillText('PRESS Z TO RESTART', WIDTH / 2, HEIGHT / 2 + 44);
    }
    g.textBaseline = 'top';
    g.textAlign = 'left';
  }
}

// --- Helpers ----------------------------------------------------------

function makeEntity(r: number, c: number): Entity {
  return { r, c, dir: 'none', queuedDir: 'none' };
}

function makeThieves(): Thief[] {
  // THIEF_KINDS and THIEF_SPAWN_CELLS are both length-4 and statically
  // declared, but TS doesn't infer that from the const types — pick the
  // fallback at the call site so the array-index access is sound.
  const fallback = THIEF_SPAWN_CELLS[0] ?? { r: 1, c: 1 };
  return THIEF_KINDS.map((kind, i) => {
    const spawn = THIEF_SPAWN_CELLS[i] ?? fallback;
    return {
      r: spawn.r,
      c: spawn.c,
      dir: 'none' as Dir,
      queuedDir: 'none' as Dir,
      kind,
      spawn: { r: spawn.r, c: spawn.c },
      jailed: false,
      jailedTicks: 0,
      waypointIdx: 0,
    };
  });
}

function thiefColor(kind: ThiefKind): string {
  if (kind === 'pursuer') return THIEF_PURSUER;
  if (kind === 'ambusher') return THIEF_AMBUSHER;
  if (kind === 'patroller') return THIEF_PATROLLER;
  return THIEF_WANDERER;
}

function oppositeDir(d: Dir): Dir {
  if (d === 'up') return 'down';
  if (d === 'down') return 'up';
  if (d === 'left') return 'right';
  if (d === 'right') return 'left';
  return 'none';
}

function vecToDir(dr: number, dc: number): Dir {
  if (dr === -1 && dc === 0) return 'up';
  if (dr === 1 && dc === 0) return 'down';
  if (dr === 0 && dc === -1) return 'left';
  if (dr === 0 && dc === 1) return 'right';
  return 'none';
}

function isAtCellCenter(e: Entity): boolean {
  return Math.abs(e.r - Math.round(e.r)) < 0.05 && Math.abs(e.c - Math.round(e.c)) < 0.05;
}

// Re-export the type so callers in test files can name it if needed.
export type { CellKindValue };
